import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchMembershipProductsLite } from '../data';

const PAGE_LIMIT = 100; // backend max

// Cursor-paginated SKU search + infinite scroll for antd Select (mode="multiple").
// Reused by Gift SKUs (StepBenefitMechanic) and Included/Excluded SKUs (StepProducts).
const useSkuSearch = ({ storeLabel, selectedSkus = [] }) => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const runSearch = (nextSearch) => {
    if (!storeLabel) {
      setItems([]);
      setCursor(null);
      setHasMore(false);
      return;
    }
    const requestId = (requestIdRef.current += 1);
    setLoading(true);
    fetchMembershipProductsLite({ storeLabel, search: nextSearch, limit: PAGE_LIMIT })
      .then(({ items: fetched, nextCursor, hasMore: more }) => {
        if (requestId !== requestIdRef.current) return;
        setItems(fetched);
        setCursor(nextCursor);
        setHasMore(more);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setItems([]);
        setCursor(null);
        setHasMore(false);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  };

  const onSearch = (value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), 300);
  };

  const loadMore = () => {
    if (!hasMore || !cursor || loadingMore || loading) return;
    setLoadingMore(true);
    fetchMembershipProductsLite({ storeLabel, search, cursor, limit: PAGE_LIMIT })
      .then(({ items: fetched, nextCursor, hasMore: more }) => {
        setItems((current) => [...current, ...fetched]);
        setCursor(nextCursor);
        setHasMore(more);
      })
      .catch(() => {
        setHasMore(false);
      })
      .finally(() => setLoadingMore(false));
  };

  // Reset and load the first page whenever the store changes.
  useEffect(() => {
    setSearch('');
    runSearch('');
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeLabel]);

  const options = useMemo(() => {
    const bySku = new Map();
    items.forEach((item) => {
      if (item.sku) bySku.set(item.sku, item);
    });
    // Keep already-selected SKUs visible even if they fall out of the current search results/page.
    selectedSkus.forEach((sku) => {
      if (!bySku.has(sku)) bySku.set(sku, { sku });
    });
    return Array.from(bySku.values()).map((item) => ({
      value: item.sku,
      label: (
        <span style={{ color: item.is_visible === false ? '#ff4d4f' : undefined }}>
          {item.name ? `${item.sku} — ${item.name}` : item.sku}
        </span>
      ),
    }));
  }, [items, selectedSkus]);

  const onPopupScroll = (e) => {
    const target = e.target;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 24) {
      loadMore();
    }
  };

  return { options, onSearch, onPopupScroll, loading, loadingMore, hasMore };
};

export default useSkuSearch;
